import {
  Box,
  Button,
  IconButton,
  DialogActions,
  DialogContent,
  LinearProgress,
  Stack,
  Typography,
  styled,
  Tooltip,
} from '@mui/material';
import {QUALITY_CLASS_COLOR, SUPPORTED_QUALITY_CLASSES} from 'appConstants';
import DialogTitleWithClose from 'components/Generic/DialogTitle';
import GenericDialog from 'components/Generic/GenericDialog';
import GenericTextField from 'components/Generic/TextField';
import NumberFormatCustom from 'helpers/NumberFormatter';
import type {IQualityGrading, ModalDialogProps} from 'interfaces';
import {useState, type FC} from 'react';
import {useTranslation} from 'react-i18next';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {getQualityModelTotal} from 'components/InputMenu/QualityModels/QualityModelsTable.helpers';

export const FishGroupQualityModal: FC<ModalDialogProps<any, {parameters: IQualityGrading}>> = ({
  dialogProps,
  modalComponentProps: {
    onBeforeClose,
    formDataPayload: {parameters},
  },
}) => {
  const [qualityParameter, setQualityParameters] = useState<IQualityGrading>(normalizeModel(parameters, 100));
  const total = getQualityModelTotal(qualityParameter);
  const {t} = useTranslation('common');

  const closeModal = () => {
    if (dialogProps.onClose) {
      dialogProps.onClose({}, 'escapeKeyDown');
    }
  };

  const sendModel = () => {
    onBeforeClose && onBeforeClose({parameters: normalizeModel(qualityParameter, 0.01)});
    dialogProps.onClose && dialogProps.onClose({}, 'escapeKeyDown');
  };

  return (
    <GenericDialog {...dialogProps} data-testid="fishgroup-quality-modal">
      <DialogTitleWithClose
        onClose={() => {
          closeModal();
        }}
      >
        {t('overwriteQualityDistribution')}
      </DialogTitleWithClose>
      <DialogContent sx={{padding: 0, paddingX: 3, minWidth: '400px'}}>
        <Stack gap={2} pt={2} width={'100%'}>
          {SUPPORTED_QUALITY_CLASSES.map((qualityClass) => (
            <Box key={qualityClass} sx={{position: 'relative'}} width={'100%'}>
              <GenericTextField
                data-testid={'quality-class-' + qualityClass}
                inputProps={{
                  inputMode: 'decimal',
                  step: 'any',
                }}
                InputProps={{
                  inputComponent: NumberFormatCustom as (props) => JSX.Element,
                  endAdornment: (
                    <Stack direction={'row'} spacing={1} alignItems={'center'}>
                      <Tooltip title={t('fill')}>
                        <IconButton
                          size={'small'}
                          color={'secondary'}
                          onClick={() => {
                            const newValue =
                              total >= 100
                                ? qualityParameter[qualityClass] - (total - 100)
                                : qualityParameter[qualityClass] + 100 - total;
                            setQualityParameters({...qualityParameter, [qualityClass]: newValue});
                          }}
                        >
                          {total > 100 && <VerticalAlignBottomIcon fontSize="inherit" />}
                          {total < 100 && <VerticalAlignTopIcon fontSize="inherit" />}
                          {total === 100 && <CheckCircleIcon fontSize="inherit" color="success" />}
                        </IconButton>
                      </Tooltip>
                      <Typography>%</Typography>
                    </Stack>
                  ),
                  disableUnderline: true,
                }}
                label={t(qualityClass)}
                value={qualityParameter[qualityClass]}
                onChange={(event) => {
                  let value = parseFloat(event.target.value);
                  if (isNaN(value)) {
                    value = 0;
                  }
                  const newValue = value > 100 ? 100 : value;
                  setQualityParameters({...qualityParameter, [qualityClass]: newValue});
                }}
              />
              <LinearProgress
                color={QUALITY_CLASS_COLOR[qualityClass] as any}
                variant="determinate"
                value={qualityParameter[qualityClass]}
                sx={{position: 'absolute', bottom: '0px', height: '3px', width: '100%'}}
              />
            </Box>
          ))}
          <QualityTotal value={total} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{paddingX: 3}}>
        <Button
          disabled={isInvalid(qualityParameter)}
          variant="contained"
          onClick={sendModel}
          data-testid="override-quality-model"
        >
          {t('overwrite')}
        </Button>
      </DialogActions>
    </GenericDialog>
  );
};

const isInvalid = (parameters: IQualityGrading) => {
  return (
    getQualityModelTotal(parameters) !== 100 ||
    SUPPORTED_QUALITY_CLASSES.some((qualityClass) => isNaN(parameters[qualityClass]))
  );
};

const normalizeModel = (model: IQualityGrading, scaler: number) => ({
  production: model.production * scaler,
  vrak: model.vrak * scaler,
  ordinary: model.ordinary * scaler,
  superior: model.superior * scaler,
});

const QualityTotal = ({value}: {value: number}) => {
  const {t} = useTranslation('common');
  const isError = value !== 100;

  return (
    <StyledQualityTotalStack direction="row" gap={1} error={isError}>
      {isError ? <HighlightOffIcon /> : <CheckCircleOutlineIcon />}
      <Typography>{`${t('total')}: ${value}%`}</Typography>
    </StyledQualityTotalStack>
  );
};

const StyledQualityTotalStack = styled(Stack)<{error: boolean}>(({theme, error}) => ({
  borderRadius: theme.spacing(0.5),
  border: `1px solid ${error ? theme.palette.error.main : theme.palette.success.main}`,
  padding: `${theme.spacing(2)} ${theme.spacing(1)}`,
  color: error ? theme.palette.error.main : theme.palette.success.main,
  alignItems: 'center',
  '&>p': {
    color: error ? theme.palette.error.light : theme.palette.success.light,
  },
}));
